import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
var relativeTime = require('dayjs/plugin/relativeTime')
dayjs.extend(relativeTime)
import { Link, useSearchParams, useNavigate, useParams } from 'react-router-dom'
import { Formik, Field } from 'formik'
import * as UpChunk from '@mux/upchunk'
import { colors, getDisplaySize } from '../../../ui/helpers.js'
import { H1, H2, H3, H6, Text, Span, GradientFont, Error, SmallTitle } from '../../../ui/Typography.js'
import { Flex, Box, Container } from '../../../ui/Layout.js'
import Image from '../../../ui/Image.js'
import { Input, TextArea } from '../../../ui/Input.js'
import Icon from '../../../ui/Icon.js'
import CodeCopy from '../../../ui/CodeCopy.js'
import VideoPreview from '../../../ui/VideoPreview.js'
import CarouselPreview from '../../../Components/CarouselPreview.js'

import WidgetPreview from '../../../Components/WidgetPreview.js'
import Button from '../../../ui/Button.js'
import { BigDarkSpinner } from '../../../ui/Spinner.js'
import Toggle from '../../../ui/Toggle.js'
import Select from '../../../ui/Select.js'
import ColorPicker from '../../../ui/ColorPicker.js'
import formikFormWrapper from '../../../formikFormWrapper.js'
import { toast } from 'react-toastify'
import { useModal } from '../../../Modal.js'
import ModalContainer from '../../../ui/ModalContainer.js'
import ChatButton from '../../../Components/ChatButton.js'
import Tooltip from '../../../ui/Tooltip.js'

const FormInput = formikFormWrapper(Input)

import api from '../../../api.js'
import { useAuth } from '../../../Auth.js'

import {
  STEPS,
  WIDGET_TYPES,
  WIDGET_THUMBNAIL_DIMENSIONS,
  TITLE_POSITION,
  TITLE_ALIGNMENT,
  WIDGET_THUMBNAIL_SIZE,
  getSelectNavigationWidgetOptions,
  getSelectInlineWidgetOptions,
  getSelectWidgetOptions
} from '../../types.js'
import { isScalePlan } from '../../../Plans.js'
import InlineCarousel from '../../../Components/InlineCarousel.js'
import { inlineTemplates, templateSubtitles, templateTitles, templateVideoObjects, templateVideos } from '../utils/templates.js'
import { FakeDesktop } from '../../../Components/EmbedPreview.js'

const ChoiceContainer = styled(Container)`
  ${(props) => (props.disabled ? 'background: rgba(250, 250, 250, 1);cursor: initial !important;' : '')}
  padding: 8px;
  cursor: pointer;
  width: 30%;
  max-width: 350px;
  align-items: center;
  margin-left: 30px;
  margin-right: 30px;
  &&:hover {
    box-shadow: 0px 0px 1px rgba(12, 26, 75, 0.2), 0px 4px 20px -2px rgba(50, 50, 71, 0.2);
  }
`

const InnerContainer = styled(Flex)`
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  height: 135px;
  background: linear-gradient(225deg, #f1f1f5 0%, #e4ecf7 100%);
  margin-bottom: 24px;
  position: relative;
`

const UpgradeContainer = styled(Flex)`
  background: linear-gradient(225deg, #bb65ff 0%, #4c6fff 100%);
  height: 37px;
  box-sizing: border-box;
  align-items: center;
  border-radius: 8px;
  width: 90px;
  justify-content: center;
  position: absolute;
  top: 8px;
  right: 8px;
`

const TemplateGrid = styled(Flex)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  width: 100%;
`

const TemplateCard = styled(Flex)`
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e2e8f0;
  background: #fafbfd;
  box-shadow: 0px 0px 1px 0px rgba(12, 26, 75, 0.1), 0px 4px 20px -2px rgba(50, 50, 71, 0.08);
  aspect-ratio: 4/3;
  position: relative;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`

const PreviewContainer = styled(Flex)`
  max-width: 600px;
  padding-top: 40px;
  padding-bottom: 40px;
`

const InlineTemplates = ({
  step,
  setStep,
  values,
  selectedVideos,
  openFullscreenDesktopPreview,
  openFullscreenMobilePreview,
  videoObjects,
  setFieldValue,
  widgets,
  submit
}) => {
  const { user } = useAuth()
  const { setShowInstallModal, setInstallModalCampaign } = useModal()
  const [search, setSearch] = useSearchParams()
  const previousPage = search.get('previousPage')
  const [activeTemplate, setActiveTemplate] = useState(inlineTemplates[0])
  const scrollContainerRef = useRef(null)

  const [showIntroModal, setShowIntroModal] = useState(false)
  const [showWidgetSelectModal, setShowWidgetSelectModal] = useState(false)
  const widgetModalOptions = values.isNavigationCampaign
    ? getSelectNavigationWidgetOptions(widgets)
    : values.isInlineCampaign
    ? getSelectInlineWidgetOptions(widgets)
    : getSelectWidgetOptions(widgets)

  const navigate = useNavigate()
  useEffect(() => {
    if (search.get('isCreate')) {
      setShowIntroModal(true)
    }
  }, [])

  // FEW OF THESE ARE USED
  const createWidgetFromTemplate = async () => {
    const createWidgetsRes = await api.post(`/campaign/widget/create`, {
      ...activeTemplate,
      name: `Widget - ${widgets.length + 1}`
    })
    setFieldValue('widgetId', createWidgetsRes.data.payload.id)
    values.widgetId = createWidgetsRes.data.payload.id
    await submit()
    navigate(`/campaigns/editor/${values.id}?showInstallModal=true&showHelpModal=true`)
  }

  const createBlankWidget = async () => {
    if (!values.widget.name) {
      values.widget.name = `Widget - ${widgets.length + 1}`
    }
    values.widget.type = WIDGET_TYPES.INLINE_CAROUSEL
    const createWidgetsRes = await api.post(`/campaign/widget/create`, values.widget)
    setFieldValue('widgetId', createWidgetsRes.data.payload.id)
    values.widgetId = createWidgetsRes.data.payload.id
    await submit()
    navigate(`/campaigns/editor/${values.id}?showInstallModal=true&showHelpModal=true`)
  }

  return (
    <>
      <Flex height="100vh" flexDirection="column" alignItems="center">
        <Flex height="100vh">
          <Flex flexDirection="column" height="100%" width="305px" px="24px">
            <Flex
              onClick={() => setStep(previousPage ? previousPage : STEPS.CAMPAIGN_TYPE, { nextPage: STEPS.WIDGET_TYPE })}
              cursor="pointer"
              width="auto"
              height="80px"
              alignItems="center"
              mb="16px"
            >
              <Icon mr="16px" icon="chevron-left-lightlight" height="16px" width="16px" />
              <Text lightLight fontSize="14px" textAlign="left">
                Back
              </Text>
            </Flex>
            <Text fontSize="24px" fontWeight="600" mb="16px">
              Start with a template
            </Text>
            {widgets && widgets.length ? (
              <>
                <Text light lineHeight="130%" mb="16px">
                  or
                </Text>
                <Text cursor="pointer" onClick={() => setShowWidgetSelectModal(true)} light fontWeight="600" underline>
                  use an existing design of yours
                </Text>
              </>
            ) : null}
          </Flex>
          <Flex
            ref={scrollContainerRef}
            flexDirection="column"
            height="100vh"
            width="calc(100% - 305px)"
            backgroundColor="#F7FAFC"
            overflowY="scroll"
          >
            <Flex height="80px" minHeight="80px" alignItems="center" px="24px" justifyContent="flex-end">
              <Text fontSize="14px" cursor="pointer" fontWeight="600" mr="16px" light onClick={createBlankWidget}>
                Start from scratch
              </Text>
              <Button onClick={createWidgetFromTemplate} variant="black" label="Customise this template" />
            </Flex>
            <Flex flexDirection="column" alignItems="center">
              <PreviewContainer>
                <FakeDesktop mt="0px">
                  <Flex
                    isStories={false}
                    id="__clipara-embed"
                    justifyContent="center"
                    zoom="0.5"
                    minHeight={activeTemplate.idx !== 3 ? '750px' : '800px'}
                    py="24px"
                    alignItems="center"
                    px={activeTemplate.widgetIsPartialStart ? '0px' : '24px'}
                  >
                    <Flex width={`calc(100%)`}>
                      <Flex my="20px">
                        <InlineCarousel
                          key={activeTemplate.idx}
                          campaign={{
                            ...values,
                            ...activeTemplate,
                            videos: templateVideos,
                            videoIds: templateVideos.map((v) => v.id),
                            titles: templateTitles,
                            subtitles: templateSubtitles,
                            videoObjects: templateVideoObjects,
                            hideThumnailPicker: true
                          }}
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                </FakeDesktop>
              </PreviewContainer>
            </Flex>
            <Flex flexDirection="column" mb="24px" px="40px" mt="24px">
              <Text fontSize="16px" fontWeight="600" mb="24px">
                Other templates
              </Text>
              <TemplateGrid>
                {inlineTemplates
                  .filter((template) => template.idx !== activeTemplate.idx)
                  .map((v) => (
                    <TemplateCard
                      key={v.id}
                      onClick={() => {
                        setActiveTemplate(v)
                        scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
                      }}
                    >
                      <Image objectFit="contain" src={`/inline-templates/${v.idx}.jpg`} width="100%" height="100%" />
                    </TemplateCard>
                  ))}
              </TemplateGrid>
            </Flex>
          </Flex>
        </Flex>
        <ModalContainer
          overflowY="undefined"
          interiorClose
          p="24px"
          width="500px"
          height="300px"
          isOpen={showWidgetSelectModal}
          setIsOpen={setShowWidgetSelectModal}
        >
          <H3 width="100%">Select Existing Widget</H3>
          <Select
            mb="8px"
            fullWidth
            value={widgets && widgets.find((w) => w.id === values.widgetId)}
            onChange={(option) => {
              setFieldValue(`widgetId`, option.value)
            }}
            options={widgetModalOptions}
            placeholder="Select"
          />
          <Button
            onClick={async () => {
              await submit()
              navigate(`/campaigns/editor/${values.id}?showInstallModal=true&showHelpModal=true`)
            }}
            variant="black"
            label="Use this widget"
            position="absolute"
            bottom="24px"
            right="24px"
          />
        </ModalContainer>
      </Flex>
    </>
  )
}

export default InlineTemplates
